'use client';

import StreetInput, { type StreetInputProps } from '@/components/streetInput/StreetInput';
import { resolveStreetFromCatalog } from '@/helper/streetCatalog';
import { useField } from 'formik';
import { useLayoutEffect, useRef } from 'react';

type FormikStreetFieldProps = Omit<
  StreetInputProps,
  'error' | 'helperText' | 'onBlur' | 'onStreetChange' | 'onValueChange' | 'value'
>;

export default function FormikStreetField(props: FormikStreetFieldProps) {
  const [field, meta, helpers] = useField<string>(props.name);
  const currentValueRef = useRef(field.value ?? '');
  const currentLocationRef = useRef({ postcode: props.postcode, city: props.city });
  const blurRequestRef = useRef(0);

  useLayoutEffect(() => {
    currentValueRef.current = field.value ?? '';
  }, [field.value]);

  useLayoutEffect(() => {
    currentLocationRef.current = { postcode: props.postcode, city: props.city };
    blurRequestRef.current += 1;
  }, [props.postcode, props.city]);

  return (
    <StreetInput
      {...props}
      value={field.value ?? ''}
      error={meta.touched && Boolean(meta.error)}
      helperText={meta.touched && meta.error}
      onValueChange={(value) => {
        currentValueRef.current = value;
        blurRequestRef.current += 1;
        helpers.setError(undefined);
        void helpers.setValue(value, false);
      }}
      onStreetChange={(street) => {
        currentValueRef.current = street ?? '';
        blurRequestRef.current += 1;
        helpers.setError(undefined);
        void helpers.setValue(street ?? '', Boolean(street));
        void helpers.setTouched(Boolean(street), false);
      }}
      onBlur={() => {
        const requestedValue = currentValueRef.current;
        const requestedLocation = currentLocationRef.current;
        const requestId = ++blurRequestRef.current;

        void helpers.setTouched(true, false);
        void (async () => {
          const canonicalStreet = await resolveStreetFromCatalog(requestedLocation, requestedValue).catch(() => null);

          const currentLocation = currentLocationRef.current;
          const requestIsCurrent =
            blurRequestRef.current === requestId &&
            currentValueRef.current === requestedValue &&
            currentLocation.postcode === requestedLocation.postcode &&
            currentLocation.city === requestedLocation.city;

          if (!requestIsCurrent) return;

          if (canonicalStreet && canonicalStreet !== requestedValue) {
            currentValueRef.current = canonicalStreet;
            await helpers.setValue(canonicalStreet, false);
          }

          await helpers.setTouched(true, true);
        })();
      }}
    />
  );
}
